// Pure helpers for the V3 organisation views (M101–M103); no network, no storage. All endpoints are proposals (V.1, D-100–D-104).
import { own, text, lookup } from "./memberDerive";
import { safeMediaUrl } from "./v3Messages";
import { dateDe } from "./v3Settings";

const isRec = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const clean = (v) => { const s = text(v); return typeof s === "string" ? s.trim() : ""; };
const orgId = (v) => (typeof v === "number" && Number.isSafeInteger(v) ? String(v) : typeof v === "string" && v.trim() && !/[\u0000-\u001f\u007f/\\]/.test(v) ? v : "");
const strings = (v) => (Array.isArray(v) ? v.map(clean).filter(Boolean) : []);
const EMAIL = /^[^\s@<>"]+@[^\s@<>"]+\.[^\s@<>"]+$/;
const PHONE = /^\+?[0-9 ()/-]{6,20}$/;

function summary(o) {
  if (!isRec(o)) return null;
  const id = orgId(own(o, "id"));
  const name = clean(own(o, "name"));
  return id && name ? { id, name, city: clean(own(o, "city")), topics: strings(own(o, "topics")) } : null;
}

// GET /member/organizations (D-100): only entries with id and name; a broken shape is null.
export function normalizeOrganizations(d) {
  const list = own(d, "organizations");
  return Array.isArray(list) ? list.map(summary).filter(Boolean) : null;
}

export const orgHref = (o, sub = "") => `/organisationen/${encodeURIComponent(o.id)}${sub}`;
export const orgInitial = (o) => Array.from(o.name)[0].toLocaleUpperCase("de");
export const orgContext = (o) => [o.city, o.topics.join(", ")].filter(Boolean).join(" · ") || "Keine Angaben zu Ort und Angebot";

const SOURCE = { self: "Eigene Angaben", verified: "Von VISIBILIS geprüfte Angaben" };
function image(v) {
  const url = safeMediaUrl(own(v, "url"));
  const alt = clean(own(v, "alt_text"));
  return url && alt ? { url, alt } : null;
}
// Offers are only listed when the Core sends the field (D-104); a missing field is null, never an empty promise.
function offers(v) {
  if (!Array.isArray(v)) return null;
  return v.filter(isRec).map((x) => ({ id: orgId(own(x, "id")), title: clean(own(x, "title")), activityId: orgId(own(x, "activity_id")) })).filter((x) => x.id && x.title);
}
function locations(v) {
  if (!Array.isArray(v)) return [];
  return v.filter(isRec).map((l) => ({ city: clean(own(l, "city")), address: clean(own(l, "address")) })).filter((l) => l.city);
}

// GET /member/organizations/{id} (D-101): public details only; `following` stays unknown (null) unless the Core sends a boolean (D-102).
export function normalizeOrganization(d) {
  const o = own(d, "organization");
  const s = summary(o);
  if (!s) return null;
  const c = own(o, "contact");
  const src = own(o, "source");
  const email = clean(own(c, "email"));
  const phone = clean(own(c, "phone"));
  const f = own(o, "following");
  return { ...s, description: clean(own(o, "description")), offer: clean(own(o, "offer_summary")), locations: locations(own(o, "locations")),
    contact: { email: EMAIL.test(email) ? email : "", phone: PHONE.test(phone) ? phone : "" }, image: image(own(o, "image")),
    source: { kind: lookup(SOURCE, own(src, "kind")) || "", updatedAt: dateDe(own(src, "updated_at")) },
    following: typeof f === "boolean" ? f : null, offers: offers(own(o, "offers")) };
}

export const locationsText = (o) => [...new Set(o.locations.map((l) => l.city))].join(" · ") || "Keine Standorte angegeben";
export const locationLine = (l) => [l.city, l.address].filter(Boolean).join(" · ");
export const contactText = (o) => [o.contact.email, o.contact.phone].filter(Boolean).join(" · ") || "Keine öffentlichen Kontaktangaben";
export const orgSourceText = (o) => `${o.source.kind || "Herkunft nicht angegeben"} · ${o.source.updatedAt ? `Stand ${o.source.updatedAt}` : "Stand nicht angegeben"}. Eine Organisationsbestätigung ist keine Zugangszertifizierung.`;
export const followText = (f) => (f === true ? "Du folgst dieser Organisation" : f === false ? "Du folgst noch nicht" : "Noch nicht verfügbar");
